import { useState } from "react";
import React, { useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth";
import { Plus, MapPin, Pencil, Trash2, Copy } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { queryClient } from "@/lib/queryClient";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import * as groupToursService from "@/services/groupTours";
import groupPackages1 from "@/pages/group-tours/data/groupPackages1";
import { getGroupTourImageUrl } from "@/environments/environment";

interface GroupTour {
    _id: string;
    tourTitle: string;
    category: string;
    description: string;
    imageUrl: string;
    tags: string[];
    rating: number;
    reviews: number;
    inclusive: string[];
    price: {
        currency: string;
        total: number;
        emi: number;
        emiFrequency: string;
        personType: string;
    };
    destinations: {
        days: number;
        countries: Array<{
            name: string;
            city: string[];
        }>;
    };
    depatures: Array<{
        city: string;
        date: string;
    }>;
    highlights: string[];
    itinerary: Array<{
        day: number;
        title: string;
        description: string;
    }>;
    isActive: boolean;
    createdAt: string;
}

export default function AdminGroupToursPage() {
    const { hasPermission } = useAuth() as any;
    const { toast } = useToast();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTour, setEditingTour] = useState<GroupTour | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<GroupTour | null>(null);
    const [itineraryItems, setItineraryItems] = useState<any[]>([]);
    const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

    // API base for constructing full image URLs

    // Sync itinerary items when editingTour changes
    useEffect(() => {
        if (editingTour && editingTour.itinerary) {
            setItineraryItems(editingTour.itinerary);
        } else if (!editingTour) {
            setItineraryItems([]);
        }
    }, [editingTour]);

    // Load sample template into form
    const loadSampleTemplate = (sample: any) => {
        const itinerary = sample.itinerary || [];
        setItineraryItems(itinerary);
        setEditingTour({
            _id: "",
            tourTitle: sample.tourTitle,
            category: sample.category,
            description: sample.description,
            imageUrl: sample.imageUrl,
            tags: sample.tags || [],
            rating: sample.rating || 0,
            reviews: sample.reviews || 0,
            inclusive: sample.inclusive || [],
            price: sample.price || { currency: "INR", total: 0, emi: 0, emiFrequency: "monthly", personType: "per person on twin sharing" },
            destinations: sample.destinations || { days: 0, countries: [] },
            depatures: sample.depatures || [],
            highlights: sample.highlights || [],
            itinerary: itinerary,
            isActive: true,
            createdAt: new Date().toISOString(),
        } as GroupTour);
        setDialogOpen(true);
        toast({ title: "Template loaded", description: "Edit and save to create a new tour" });
    };

    const { data: toursData = [], isLoading } = useQuery<GroupTour[]>({
        queryKey: ["/v1/group-tours"],
        queryFn: async () => {
            try {
                const result = await groupToursService.listGroupTours();
                return result.results || result.data || result || [];
            } catch (error) {
                toast({
                    title: "Failed to load group tours",
                    description: error instanceof Error ? error.message : "Unknown error",
                    variant: "destructive",
                });
                return [];
            }
        },
    });

    const canWrite = hasPermission("manageGroupTours", "write");

    const createMutation = useMutation({
        mutationFn: async (data: any) => {
            return await groupToursService.createGroupTour(data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["/v1/group-tours"] });
            toast({ title: "Group tour created successfully" });
            setDialogOpen(false);
        },
        onError: (error: Error) => {
            toast({
                title: "Failed to create group tour",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const updateMutation = useMutation({
        mutationFn: async ({ id, data }: { id: string; data: any }) => {
            return await groupToursService.updateGroupTour(id, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["/v1/group-tours"] });
            toast({ title: "Group tour updated successfully" });
            setDialogOpen(false);
            setEditingTour(null);
        },
        onError: (error: Error) => {
            toast({
                title: "Failed to update group tour",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (payload: { id: string }) => {
            const id = payload?.id;
            return await groupToursService.deleteGroupTour(id);
        },
        onSuccess: (_data, variables) => {
            const deletedId = (variables as any)?.id;
            // Optimistically remove the deleted item from cache if present
            try {
                queryClient.setQueryData(["/v1/group-tours"], (old: any) => {
                    if (!old) return old;
                    // If result is a plain array
                    if (Array.isArray(old)) {
                        return old.filter((it: any) => (it._id || it.id) !== deletedId);
                    }
                    // If paginated result with `results` array
                    if (old.results && Array.isArray(old.results)) {
                        return { ...old, results: old.results.filter((it: any) => (it._id || it.id) !== deletedId) };
                    }
                    return old;
                });
            } catch (e) {
                // ignore cache update errors
            }

            queryClient.invalidateQueries({ queryKey: ["/v1/group-tours"] });
            toast({ title: "Group tour deleted successfully" });
        },
        onError: (error: Error) => {
            toast({
                title: "Failed to delete group tour",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const rawForm = new FormData(e.currentTarget);

        const destinations = {
            days: parseInt(rawForm.get("days") as string),
            countries: JSON.parse((rawForm.get("countries") as string) || "[]"),
        };

        const depatures = JSON.parse((rawForm.get("depatures") as string) || "[]");

        // Remove _id from itinerary items before submission
        const cleanItinerary = itineraryItems.map(({ _id, ...rest }) => rest);

        const plainData = {
            tourTitle: (rawForm.get("tourTitle") as string) || "",
            category: (rawForm.get("category") as string) || "",
            description: (rawForm.get("description") as string) || "",
            imageUrl: (rawForm.get("imageUrl") as string) || editingTour?.imageUrl || "",
            tags: ((rawForm.get("tags") as string) || "").split(",").filter((t) => t.trim()).map((t) => t.trim()),
            rating: parseFloat((rawForm.get("rating") as string) || "") || 0,
            reviews: parseInt((rawForm.get("reviews") as string) || "") || 0,
            inclusive: ((rawForm.get("inclusive") as string) || "").split(",").filter((i) => i.trim()).map((i) => i.trim()),
            price: {
                currency: (rawForm.get("currency") as string) || "INR",
                total: parseFloat((rawForm.get("total") as string) || "") || 0,
                emi: parseFloat((rawForm.get("emi") as string) || "") || 0,
                emiFrequency: (rawForm.get("emiFrequency") as string) || "monthly",
                personType: (rawForm.get("personType") as string) || "per person on twin sharing",
            },
            destinations,
            depatures,
            highlights: ((rawForm.get("highlights") as string) || "").split(",").filter((h) => h.trim()).map((h) => h.trim()),
            itinerary: cleanItinerary,
            isActive: rawForm.get("isActive") === "true",
        };

        const imageFile = rawForm.get("image") as File | null;

        if (imageFile && imageFile.size > 0) {
            const formDataToSend = new FormData();
            formDataToSend.append("image", imageFile);
            formDataToSend.append("tourTitle", plainData.tourTitle);
            formDataToSend.append("category", plainData.category);
            formDataToSend.append("description", plainData.description);
            formDataToSend.append("imageUrl", plainData.imageUrl);
            formDataToSend.append("tags", JSON.stringify(plainData.tags));
            formDataToSend.append("rating", String(plainData.rating));
            formDataToSend.append("reviews", String(plainData.reviews));
            formDataToSend.append("inclusive", JSON.stringify(plainData.inclusive));
            formDataToSend.append("price", JSON.stringify(plainData.price));
            formDataToSend.append("destinations", JSON.stringify(plainData.destinations));
            formDataToSend.append("depatures", JSON.stringify(plainData.depatures));
            formDataToSend.append("highlights", JSON.stringify(plainData.highlights));
            formDataToSend.append("itinerary", JSON.stringify(plainData.itinerary));
            formDataToSend.append("isActive", String(plainData.isActive));

            if (editingTour && editingTour._id) {
                updateMutation.mutate({ id: editingTour._id, data: formDataToSend });
            } else {
                createMutation.mutate(formDataToSend);
            }
        } else {
            if (editingTour && editingTour._id) {
                updateMutation.mutate({ id: editingTour._id, data: plainData });
            } else {
                createMutation.mutate(plainData);
            }
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Group Tours</h1>
                    <p className="text-muted-foreground mt-2">Manage group tour packages and schedules</p>
                </div>
                {canWrite && (
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button onClick={() => setEditingTour(null)} disabled={createMutation.isPending}>
                                <Plus className="mr-2 h-4 w-4" />
                                Add Group Tour
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle>{editingTour ? "Edit Group Tour" : "Create New Group Tour"}</DialogTitle>
                                <DialogDescription>{editingTour ? "Update tour information" : "Add a new group tour package"}</DialogDescription>
                            </DialogHeader>

                            {/* Sample Templates Section */}
                            {!editingTour && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                    <p className="text-sm font-semibold text-blue-900 mb-3">📋 Use Sample Templates:</p>
                                    <div className="flex gap-2">
                                        {groupPackages1.map((sample: any) => (
                                            <Button
                                                key={sample.id}
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => loadSampleTemplate(sample)}
                                                className="flex items-center gap-2"
                                            >
                                                <Copy className="w-3 h-3" />
                                                {sample.tourTitle}
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="tourTitle">Tour Title *</Label>
                                        <Input id="tourTitle" name="tourTitle" defaultValue={editingTour?.tourTitle} required />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="category">Category *</Label>
                                        <Input id="category" name="category" placeholder="e.g., Family, Adventure" defaultValue={editingTour?.category} required />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="description">Description *</Label>
                                    <Textarea id="description" name="description" defaultValue={editingTour?.description} rows={3} required />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="image">Image Upload</Label>
                                        <Input id="image" name="image" type="file" accept="image/*" />
                                        <input type="hidden" name="imageUrl" value={editingTour?.imageUrl || ""} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="rating">Rating (0-5)</Label>
                                        <Input id="rating" name="rating" type="number" min="0" max="5" step="0.1" defaultValue={editingTour?.rating} />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="reviews">Reviews</Label>
                                        <Input id="reviews" name="reviews" type="number" min="0" defaultValue={editingTour?.reviews} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="days">Duration (days) *</Label>
                                        <Input id="days" name="days" type="number" min="1" defaultValue={editingTour?.destinations?.days} required />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="tags">Tags (comma-separated)</Label>
                                    <Input id="tags" name="tags" placeholder="Group Tour, Family, Popular" defaultValue={editingTour?.tags?.join(", ")} />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="inclusive">Inclusive (comma-separated) *</Label>
                                    <Input id="inclusive" name="inclusive" placeholder="Hotels, Food, Flight, Transport" defaultValue={editingTour?.inclusive?.join(", ")} required />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="highlights">Highlights (comma-separated)</Label>
                                    <Input id="highlights" name="highlights" placeholder="Landmark 1, Landmark 2" defaultValue={editingTour?.highlights?.join(", ")} />
                                </div>

                                <div className="border-t pt-4">
                                    <h3 className="font-semibold mb-2">Pricing</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="currency">Currency</Label>
                                            <Input id="currency" name="currency" defaultValue={editingTour?.price?.currency || "INR"} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="total">Total Price *</Label>
                                            <Input id="total" name="total" type="number" step="0.01" defaultValue={editingTour?.price?.total} required />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="emi">EMI Amount</Label>
                                            <Input id="emi" name="emi" type="number" step="0.01" defaultValue={editingTour?.price?.emi} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="emiFrequency">EMI Frequency</Label>
                                            <Input id="emiFrequency" name="emiFrequency" defaultValue={editingTour?.price?.emiFrequency || "monthly"} />
                                        </div>
                                    </div>
                                    <div className="space-y-2 mt-4">
                                        <Label htmlFor="personType">Person Type</Label>
                                        <Input id="personType" name="personType" defaultValue={editingTour?.price?.personType || "per person on twin sharing"} />
                                    </div>
                                </div>

                                <input type="hidden" name="countries" value={JSON.stringify(editingTour?.destinations?.countries || [])} />
                                <input type="hidden" name="depatures" value={JSON.stringify(editingTour?.depatures || [])} />

                                {/* Itinerary Editor */}
                                <div className="border-t pt-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="font-semibold">Itinerary ({itineraryItems.length} days)</h3>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                                const newDay = itineraryItems.length + 1;
                                                setItineraryItems([...itineraryItems, { day: newDay, title: "", description: "" }]);
                                            }}
                                        >
                                            <Plus className="w-4 h-4 mr-1" />
                                            Add Day
                                        </Button>
                                    </div>

                                    {itineraryItems.length === 0 ? (
                                        <p className="text-sm text-gray-500 mb-3">No itinerary items yet. Click "Add Day" to create one.</p>
                                    ) : (
                                        <div className="space-y-3 mb-4">
                                            {itineraryItems.map((item: any, idx: number) => (
                                                <div key={idx} className="bg-gray-50 p-3 rounded border">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <Label className="text-sm font-semibold">Day {item.day}</Label>
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => {
                                                                setItineraryItems(itineraryItems.filter((_: any, i: number) => i !== idx));
                                                            }}
                                                        >
                                                            <Trash2 className="w-4 h-4 text-red-500" />
                                                        </Button>
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Input
                                                            placeholder="Day title (e.g., Arrival in Singapore)"
                                                            value={item.title}
                                                            onChange={(e) => {
                                                                const updated = [...itineraryItems];
                                                                updated[idx].title = e.target.value;
                                                                setItineraryItems(updated);
                                                            }}
                                                        />
                                                        <Textarea
                                                            placeholder="Day description"
                                                            rows={2}
                                                            value={item.description}
                                                            onChange={(e) => {
                                                                const updated = [...itineraryItems];
                                                                updated[idx].description = e.target.value;
                                                                setItineraryItems(updated);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-end gap-2 pt-4">
                                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setEditingTour(null); setItineraryItems([]); }}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                                        {editingTour?._id ? "Update" : "Create"} Tour
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            <Card>
                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="p-6 space-y-4">{[1, 2, 3].map((i) => (<Skeleton key={i} className="h-16 w-full" />))}</div>
                    ) : toursData && toursData.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Title</TableHead>
                                    <TableHead>Category</TableHead>
                                    <TableHead>Duration</TableHead>
                                    <TableHead>Price</TableHead>
                                    <TableHead>Rating</TableHead>
                                    <TableHead>Status</TableHead>
                                    {canWrite && <TableHead className="text-right">Actions</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {toursData.map((tour) => (
                                    <TableRow key={tour._id || (tour as any).id}>
                                        <TableCell>
                                            <div className="flex items-start gap-2">
                                                {tour.imageUrl && !failedImages.has(tour.imageUrl) ? (
                                                    <img
                                                        src={getGroupTourImageUrl(tour.imageUrl)}
                                                        alt={tour.tourTitle}
                                                        className="w-12 h-12 rounded object-cover flex-shrink-0"
                                                        onError={(e) => {
                                                            setFailedImages(prev => new Set(prev).add(tour.imageUrl));
                                                        }}
                                                    />
                                                ) : (
                                                    <MapPin className="w-12 h-12 p-2 rounded bg-muted text-muted-foreground flex-shrink-0" />
                                                )}
                                                <div>
                                                    <p className="font-medium">{tour.tourTitle}</p>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline">{tour.category}</Badge>
                                        </TableCell>
                                        <TableCell>{tour.destinations?.days} days</TableCell>
                                        <TableCell className="font-medium">₹{tour.price?.total?.toLocaleString()}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1">
                                                <i className="ri-star-fill text-yellow-400"></i>
                                                <span>{tour.rating}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={tour.isActive ? "default" : "secondary"}>
                                                {tour.isActive ? "Active" : "Inactive"}
                                            </Badge>
                                        </TableCell>
                                        {canWrite && (
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button variant="ghost" size="icon" onClick={() => { setEditingTour({ ...tour, _id: (tour as any)._id || (tour as any).id }); setDialogOpen(true); }} disabled={updateMutation.isPending}>
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => {
                                                            setDeleteTarget(tour);
                                                            setDeleteDialogOpen(true);
                                                        }}
                                                        disabled={deleteMutation.isPending}>
                                                        <Trash2 className="h-4 w-4 text-destructive" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <MapPin className="h-12 w-12 text-muted-foreground/50 mb-4" />
                            <h3 className="text-lg font-semibold">No group tours yet</h3>
                            <p className="text-sm text-muted-foreground mt-2 max-w-sm">Create and manage group tour packages.</p>
                        </div>
                    )}
                </CardContent>
            </Card>
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete group tour</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete <strong>{deleteTarget?.tourTitle}</strong>? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setDeleteTarget(null); }}>Cancel</Button>
                        <Button variant="destructive" onClick={() => {
                            if (deleteTarget) {
                                const idToDelete = (deleteTarget as any)._id || (deleteTarget as any).id || '';
                                if (idToDelete) deleteMutation.mutate({ id: String(idToDelete) });
                            }
                            setDeleteDialogOpen(false);
                            setDeleteTarget(null);
                        }} disabled={deleteMutation.isPending}>Delete</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
